import * as React from "react";
import { FileText } from "lucide-react";

import { SidePanel } from "@/components/chat/SidePanel";
import type { SidePanelTab, SidePanelTabConfig } from "@/components/chat/types";
import { RightDrawerDocumentName } from "@/components/feature/layout/right-sidebar/components/documentName";
import { PDFViewer } from "@/components/feature/pdf/pdfViewer";
import { Button } from "@/components/ui/button";
import { useCurrentPdf } from "@/modules/chat/hooks/useCurrentPdf";
import { SidebarPromptImprovements } from "@/modules/open-chat/components/sidebar/sidebarPromptImprovements";
import { PromptLibraryRightDrawer } from "@/modules/promptLibrary/components/promptLibraryRightDrawer";

interface Props {
  promptGeneratorConversationId: string;
  historyPanel: React.ReactNode;
  /** Rendered in the Artefacts tab while an artefact is selected. */
  artefactsPanel: React.ReactNode;
}

/**
 * Right-hand chat panel (PDF, history, artefacts, improve, library) plus the "Show Panel" button
 * shown while it is closed. Returns siblings so both stay direct children of the caller's flex row.
 */
export const ChatSidePanel = ({ promptGeneratorConversationId, historyPanel, artefactsPanel }: Props) => {
  const { activeTab, setActiveTab, setRightDrawerContent, currentPdfId, currentCitation, selectedArtefactId } =
    useCurrentPdf();

  const handleTabChange = React.useCallback(
    (tab: SidePanelTab) => {
      setActiveTab(tab);
      if (tab === "pdf") {
        setRightDrawerContent("pdf");
      } else if (tab === "artefacts") {
        setRightDrawerContent("artefacts");
      } else if (tab === "improve") {
        setRightDrawerContent("promptGenerator");
      } else if (tab === "library") {
        setRightDrawerContent("promptLibrary");
      }
    },
    [setActiveTab, setRightDrawerContent],
  );

  const handlePanelClose = React.useCallback(() => setActiveTab(null), [setActiveTab]);
  const handlePanelOpen = React.useCallback(() => setActiveTab(activeTab || "pdf"), [activeTab, setActiveTab]);
  const isPanelOpen = activeTab !== null;

  const tabs: SidePanelTabConfig[] = [
    { id: "pdf", label: "PDF", show: true },
    { id: "history", label: "History", show: true },
    { id: "artefacts", label: "Artefacts", show: !!selectedArtefactId },
    { id: "improve", label: "Improve", show: true },
    { id: "library", label: "Library", show: true },
  ];

  return (
    <>
      <SidePanel
        activeTab={activeTab}
        isOpen={isPanelOpen}
        tabs={tabs}
        onTabChange={handleTabChange}
        onClose={handlePanelClose}
      >
        {activeTab === "pdf" && currentPdfId && (
          <div className="flex flex-1 flex-col overflow-hidden">
            <RightDrawerDocumentName />
            <PDFViewer citationHighlight={currentCitation} />
          </div>
        )}
        {activeTab === "history" && historyPanel}
        {activeTab === "artefacts" && selectedArtefactId && artefactsPanel}
        {activeTab === "improve" && <SidebarPromptImprovements conversationId={promptGeneratorConversationId} />}
        {activeTab === "library" && <PromptLibraryRightDrawer source="qa" />}
      </SidePanel>

      {!isPanelOpen && (
        <Button
          variant="outline"
          size="icon"
          className="fixed right-4 top-4 h-9 w-9"
          onClick={handlePanelOpen}
          title="Show Panel"
        >
          <FileText size={20} />
        </Button>
      )}
    </>
  );
};
